/**
 * Popover Component - 버튼 아래에 붙어서 뜨는 작은 창 (헤더 / 바디 / [푸터])
 * 모양은 공통 모달(Modal)과 같은 톤(primary 헤더), 위치만 트리거 버튼 기준
 * 스타일: index.css "Popover" - popover-* 클래스
 *
 *   const [open, setOpen] = useState(false);
 *   <Popover
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     trigger={<Button onClick={() => setOpen((v) => !v)}>정보</Button>}
 *     title="워크플로우 정보"
 *     footer={<Button color="primary">정보 수정</Button>}   // 선택
 *     align="right"                                          // right(기본): 오른쪽 끝 맞춤 | left
 *   >
 *     본문
 *   </Popover>
 *
 * - 바깥 클릭 · ESC 로 닫힘 (트리거 버튼 클릭은 바깥으로 치지 않음 → 트리거에서 토글)
 * - width: 기본 420 (모달 sm 과 같은 폭), 화면이 좁으면 화면 폭에 맞춰 줄어듦
 */

import { ReactNode, useEffect, useRef } from 'react';

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  trigger: ReactNode;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  align?: 'left' | 'right';
  width?: number;
  className?: string;
}

export function Popover({
  open,
  onClose,
  trigger,
  title,
  children,
  footer,
  align = 'right',
  width = 420,
  className = '',
}: PopoverProps) {
  const wrapRef = useRef<HTMLDivElement>(null);

  // 바깥 클릭 · ESC 로 닫기
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) onClose();
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open, onClose]);

  return (
    <div className="popover-wrap" ref={wrapRef}>
      {trigger}
      {open && (
        <div
          className={`popover popover--${align} ${className}`.trim()}
          role="dialog"
          style={{ width }}
        >
          <div className="popover-header">
            <div className="popover-title">{title}</div>
            <button type="button" className="popover-close" onClick={onClose} aria-label="닫기">✕</button>
          </div>
          <div className="popover-body">{children}</div>
          {footer && <div className="popover-footer">{footer}</div>}
        </div>
      )}
    </div>
  );
}

export default Popover;
